import { merge } from '../../helper';
import isEmptyArray from '../../general/isEmpty/isEmptyArray';
import isObject from '../../general/isObject';

import exExternal from '../../external';
import exLanguage from '../../language';
import * as exDatatable from '../datatable';
import * as exTable from '../table';
import * as exElement from '../../utils/element';

// ----------------------------------------------------------------------------

function start(element: HTMLElement, config: Record<string, any> = {}): void {
    // If there is no element (table)
    if (!(element instanceof HTMLTableElement)) {
        console.error('Is not an element table...');
        return;
    }

    // Default Config
    const defConfig: Record<string, any> = {
        tbody: {},
        folder: 'data',
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    // Read
    read(element, defConfig);

    // Data
    data(element, defConfig);
}

// ----------------------------------------------------------------------------

function read(element: HTMLElement, config: Record<string, any> = {}): void {
    exDatatable.operation.read(element, config);
}

function data(element: HTMLElement, config: Record<string, any> = {}): void {
    // If there is no column count
    if (!config.columnCount) {
        console.error('Column count is empty...');
        return;
    }
    // If there is no data
    if (!config.data?.length) {
        return;
    }

    // ------------------------------------------------------------------------

    // Create element (table body)
    const elTableBody = exTable.alreadyTableBody(element, config.tbody);

    // --------------------------------------------------------------------

    //
    const src: Record<string, any> = {
        list: [],
    };

    // Function Callback
    const fnCallback = function (data, parent, config: Record<string, any> = {}): any {
        // Foreach Data
        for (let r = 0; r < data.length; r++) {
            const itemData = data[r];

            // Get id
            let id = r.toString();

            // Create element (row)
            const elRow = exElement.create({
                merge: config.tbody.row,
                tag: 'tr',
            });

            // If there is parent
            if (parent != null) {
                id = parent + '-' + r;

                elRow.setAttribute('row', id);
                elRow.setAttribute('data-tt-id', id);
                elRow.setAttribute('data-tt-parent-id', parent);
            }
            // Is default
            else {
                elRow.setAttribute('row', id);
                elRow.setAttribute('data-tt-id', id);
            }

            // If item data is object
            if (isObject(itemData)) {
                // Object keys
                const keys = Object.keys(itemData);
                // Foreach Column Count
                for (let c = 0; c < config.columnCount; c++) {
                    const key = keys[c];
                    const value = itemData[key];

                    // Create element (cell)
                    const elCell = exElement.create({
                        merge: config.tbody.cell,
                        tag: 'td',
                        attr: {
                            column: c.toString(),
                        },
                    });
                    elRow.appendChild(elCell);

                    // If using columns
                    if (config.columns?.length) {
                        const itemColumn = config.columns[c];

                        // Column
                        column(elCell, {
                            column: itemColumn,
                            data: itemData,
                            value: value,
                        });
                    }
                    // Is default
                    else {
                        elCell.innerHTML = value;
                    }
                }
            }

            // List Push
            src.list.push(elRow);

            // ----------------------------------------------------------------

            // Get folder
            let folder = itemData[config.folder];
            // If folder is array
            if (Array.isArray(config.folder)) {
                const find = config.folder.find(function (item): any {
                    return itemData[item];
                });

                folder = itemData[find];
            }

            // If using folder
            if (!isEmptyArray(folder)) {
                elRow.addClass('branch');

                // If using open
                if (itemData.open === true) {
                    elRow.addClass('expanded');
                }

                // Function Callback
                fnCallback(folder, id, config);
            }
            // Is default
            else {
                elRow.addClass('leaf');
            }
        }
    };

    // Function Callback
    fnCallback(config.data, null, config);

    // ------------------------------------------------------------------------

    // If using column definitions
    columnDefs(element, {
        columnDefs: config.columnDefs,
        rows: src,
        data: config.data,
    });

    // ------------------------------------------------------------------------

    // If there is list
    if (src.list?.length) {
        elTableBody.appendChildren(src.list);
    }

    //
    exTable.refresh(element);
}

// ----------------------------------------------------------------------------

function column(element: HTMLElement, config: Record<string, any> = {}): void {
    exDatatable.operation.column(element, config);
}

function columnDefs(element: HTMLElement, config: Record<string, any> = {}): void {
    exDatatable.operation.columnDefs(element, config);
}

// ----------------------------------------------------------------------------

export { start, read, data, column, columnDefs };
